import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bed, Bath, LandPlot, Maximize2, MapPin, Sparkles, Heart, Share2, Globe, Hash, CheckSquare, Square, Users, MessageSquare, MessageCircle, Phone, Link as LinkIcon, Loader2, Star, Lock, RotateCcw, RefreshCw, Trash2, Power } from 'lucide-react';
import { Property } from '../../types/types';
import { clsx } from 'clsx';
import { StatusPicker } from './StatusPicker';
import { supabase } from '../../lib/supabase';
import { formatRelativeEs } from '../../lib/relative-time';
import { buildIntercambioInquiry, isMobileDeviceUA } from '../../lib/intercambio-message-queries';
import { shouldShowRenovarPublish } from '../../lib/publish-renewal';
import { shouldShowRenovarShare } from '../../lib/share-renewal';
import { useDisplayCurrency, useGtqRate, formatPropertyPrice, type DisplayCurrency } from '../../lib/currency';
import { whatsAppLink, toWhatsAppNumber, telLink } from '../../lib/phone';
import { ColleagueAvatar, shouldShowAgencyName } from './ColleagueAvatar';

/** Check if a date string is within N days from now */
function isWithinDays(dateStr: string | undefined, days: number): boolean {
    if (!dateStr) return false;
    const exp = new Date(dateStr);
    const now = new Date();
    const diff = (exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return diff > 0 && diff <= days;
}

/** Check if a phone string looks like a valid WhatsApp number (has digits) */
function isValidPhone(phone: string | undefined): boolean {
    if (!phone) return false;
    return phone.replace(/\D/g, '').length >= 8;
}

// Land types (Terreno, Finca) are sized in varas² (lot_size_varas) instead of
// built m². Mirrors isLandType in mobile/utils/constants.ts.
const LAND_TYPE_NAMES = ['terreno', 'finca'];
const isLandType = (name?: string | null): boolean => LAND_TYPE_NAMES.includes((name ?? '').trim().toLowerCase());

// Price per unit area: $X / m² for everything except land types (Terreno,
// Finca), whose usual unit is varas². Returns null when we can't compute
// (no price, no relevant area), so callers can hide the hint cleanly.
function formatPricePerArea(p: { price?: number | null; built_up_area?: number | null; lot_size_varas?: number | null; property_type_name?: string | null }, display: DisplayCurrency = 'USD', rate: number | null = null): string | null {
    if (!p.price || p.price <= 0) return null;
    const isLand = isLandType(p.property_type_name);
    const area = isLand ? p.lot_size_varas : p.built_up_area;
    if (!area || area <= 0) return null;
    const perArea = p.price / area;
    const unit = isLand ? 'v²' : 'm²';
    if (display === 'GTQ' && rate) return `Q${Math.round(perArea * rate).toLocaleString('es-GT')} / ${unit}`;
    return `$${Math.round(perArea).toLocaleString('en-US')} / ${unit}`;
}

function formatExpiry(dateStr: string): { text: string; urgent: boolean } {
    const now = new Date();
    const exp = new Date(dateStr);
    const diffDays = Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return { text: 'Expiró', urgent: true };
    if (diffDays <= 14) return { text: `Exp: ${diffDays} día${diffDays !== 1 ? 's' : ''}`, urgent: true };
    return { text: `Vence ${exp.toLocaleDateString('es-GT', { day: 'numeric', month: 'short' })}`, urgent: false };
}

interface PropertyCardProps {
    property: Property;
    theme?: 'light' | 'dark';
    onClick?: () => void;
    onStatusChange?: (id: string, newStatus: string /*Property['status']*/) => void;
    onPublishToggle?: (id: string, e?: React.MouseEvent) => void;
    onShareToggle?: (id: string, e?: React.MouseEvent) => void;
    isSelected?: boolean;
    onSelectToggle?: (e: React.MouseEvent) => void;
    isListMode?: boolean;
    /** Number of cross-agency matching requirements (shown when property is shared) */
    matchingCount?: number;
    onMatchingClick?: (e: React.MouseEvent) => void;
    /** When true, render the inline Destacar / Quitar destaque action over the card */
    featuredManageMode?: boolean;
    /** Whether activating a new featured slot is currently possible (any purchase has a free slot) */
    featuredSlotsAvailable?: boolean;
    /** Activate or deactivate this property's featured status */
    onToggleFeatured?: (id: string, e?: React.MouseEvent) => void;
    /** Move an already-featured property to a different destaque package */
    onSwitchFeatured?: (id: string, e?: React.MouseEvent) => void;
    /** Whether the agency holds more than one destaque package (nothing to switch to otherwise) */
    featuredCanSwitch?: boolean;
    /** True while an activate/deactivate RPC is in flight for this card */
    featuredActioning?: boolean;
    /** Restore an archived property (only shown on archived cards) */
    onRestore?: (id: string, e?: React.MouseEvent) => void;
    /** Permanently delete an archived property (only shown on archived cards) */
    onPermanentDelete?: (id: string, e?: React.MouseEvent) => void;
    /** Open the activate-draft confirm dialog. When provided AND the property
     *  is a draft, the Publicar / Compartir pills are replaced by a single
     *  "Activar" button — agents can't accidentally publish a draft without
     *  promoting it (the "stuck in borrador" bug). */
    onActivateDraft?: (id: string, e?: React.MouseEvent) => void;
}

const PropertyCardImpl: React.FC<PropertyCardProps> = ({
    property, theme = 'dark', onClick, onStatusChange, onPublishToggle, onShareToggle, isSelected, onSelectToggle, isListMode = false, matchingCount, onMatchingClick,
    featuredManageMode = false, featuredSlotsAvailable = false, onToggleFeatured, featuredActioning = false,
    onSwitchFeatured, featuredCanSwitch = false,
    onRestore, onPermanentDelete, onActivateDraft
}) => {
    const isLight = theme !== 'dark';
    const [creatingRenewal, setCreatingRenewal] = useState(false);
    const navigate = useNavigate();
    // Display-currency preference (list-header pill) + Banguat rate. Hook
    // state updates bypass the React.memo prop comparator, so cards re-render
    // on toggle without a comparator change.
    const [displayCurrency] = useDisplayCurrency();
    const gtqRate = useGtqRate();
    // UA snapshot at first render so the Call button shows on phones/tablets
    // and stays hidden on desktop (where tel: is useless).
    const [isMobileUA] = useState<boolean>(() => isMobileDeviceUA());

    // Show Verificar button only when published, expiring within 7 days, and owner has WhatsApp
    const showVerificar =
        property.publish_status === 'published' &&
        isWithinDays(property.publish_expires_at, 7) &&
        !!property.owner_id &&
        isValidPhone(property.owner?.phone);

    // Resolve cover image; may be empty for properties without photos
    const coverSrc = property.cover_photo_url || property.image || (property.gallery && property.gallery.length > 0 ? property.gallery[0] : '') || '';

    // Availability status — dot/text colors used in non-bar contexts. The bar
    // is tinted to match the property status so the card broadcasts state at a
    // glance. Intercambio cards (sharedBy set) override to neutral below since
    // the status belongs to another agency.
    const NEUTRAL_BAR = 'bg-slate-600';
    const statusMetaBase = {
        'Disponible': { dot: 'bg-emerald-400', text: 'text-emerald-400', label: 'Disponible', bar: 'bg-emerald-600' },
        'Con Reserva': { dot: 'bg-amber-400', text: 'text-amber-400', label: 'Con Reserva', bar: 'bg-amber-600' },
        'Vendido': { dot: 'bg-rose-400', text: 'text-rose-400', label: 'Vendido', bar: 'bg-rose-600' },
        'Alquilado': { dot: 'bg-blue-400', text: 'text-blue-400', label: 'Alquilado', bar: 'bg-blue-600' },
        'Fuera de mercado': { dot: 'bg-slate-500', text: 'text-slate-500', label: 'Fuera de mercado', bar: 'bg-slate-600' },
    }[(property.status || 'Disponible')] ?? { dot: 'bg-slate-400', text: 'text-slate-400', label: (property.status || 'Disponible'), bar: NEUTRAL_BAR };
    const statusMeta = property.sharedBy ? { ...statusMetaBase, bar: NEUTRAL_BAR } : statusMetaBase;

    // Archived / deleted state — applies to migrated rows where the legacy
    // record was archived (state=5) or soft-deleted, plus any V2 row in the
    // same condition. Without a visible marker these look indistinguishable
    // from active properties.
    const isArchived = (property as any).is_deleted === true || (property as any).crm_status === 'archived';
    const archivedLabel = 'Archivada';

    // Type pill accent
    const typeMeta: Record<string, { bg: string; text: string }> = {
        'Apartamento': { bg: 'bg-indigo-500/15', text: 'text-indigo-400' },
        'Casa': { bg: 'bg-violet-500/15', text: 'text-violet-400' },
        'Casa Vacacional': { bg: 'bg-cyan-500/15', text: 'text-cyan-400' },
        'Oficina': { bg: 'bg-amber-500/15', text: 'text-amber-400' },
        'Local': { bg: 'bg-rose-500/15', text: 'text-rose-400' },
        'Terreno': { bg: 'bg-emerald-500/15', text: 'text-emerald-400' },
        'Bodega': { bg: 'bg-slate-500/15', text: 'text-slate-400' },
        'Edificio': { bg: 'bg-orange-500/15', text: 'text-orange-400' },
    };
    const type = typeMeta[(property.property_type_name || property.property_type || 'Propiedad')] ?? { bg: 'bg-slate-500/15', text: 'text-slate-400' };

    if (isListMode) {
        return (
            <div
                data-tour="property-card"
                onClick={onClick}
                className={clsx(
                    "group relative flex items-center gap-4 p-4 rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer border",
                    isSelected
                        ? (isLight ? "bg-indigo-50 border-indigo-300 shadow-md" : "bg-indigo-900/20 border-indigo-500/40 shadow-lg")
                        : (isLight ? "bg-white border-slate-100 hover:shadow-md hover:border-indigo-200" : "bg-slate-900/50 border-white/5 hover:bg-slate-800/80 hover:border-indigo-500/30")
                )}
            >
                {/* Leading row-selection checkbox (list mode). Placed as a real
                    flex item rather than absolute-positioned so it doesn't
                    overlap the price at the row's top-right. */}
                {onSelectToggle && (
                    <button
                        onClick={onSelectToggle}
                        className={clsx(
                            "shrink-0 p-1.5 rounded-lg border transition-all duration-300",
                            isSelected
                                ? "bg-indigo-600 border-indigo-400 text-white shadow-md"
                                : isLight
                                    ? "bg-slate-50 border-slate-200 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200"
                                    : "bg-white/5 border-white/10 text-slate-500 hover:bg-white/10 hover:text-slate-300"
                        )}
                        aria-label={isSelected ? 'Deseleccionar propiedad' : 'Seleccionar propiedad'}
                    >
                        {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                    </button>
                )}

                {/* Thumb */}
                <div className="relative w-44 h-32 shrink-0 rounded-xl overflow-hidden border border-white/10 shadow-sm group-hover:shadow-md transition-all">
                    {coverSrc ? (
                        <img src={coverSrc} alt={property.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    ) : (
                        <div className={clsx("w-full h-full flex items-center justify-center", isLight ? "bg-slate-100 text-slate-300" : "bg-slate-800 text-slate-600")}>
                            <MapPin size={24} />
                        </div>
                    )}
                    <div className="absolute inset-0 bg-black/10 transition-opacity group-hover:opacity-0" />

                    {/* Status dot — hidden on Intercambio (not your property's status to broadcast) */}
                    {!property.sharedBy && (
                        <div className={clsx("absolute top-2 right-2 w-2.5 h-2.5 rounded-full border border-white/50 shadow-sm", statusMeta.dot)} title={(property.status || 'Disponible')} />
                    )}

                    {isArchived && (
                        <div className="absolute inset-0 bg-slate-900/55 flex items-center justify-center">
                            <span className="px-2 py-0.5 rounded bg-slate-900/80 border border-white/20 text-[9px] font-black uppercase tracking-widest text-white">
                                {archivedLabel}
                            </span>
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0 py-1">
                    <div className="flex items-center gap-2 mb-1">
                        <span className={clsx("px-2 py-0.5 rounded-chip text-[9px] font-bold uppercase tracking-wider", type.bg, type.text)}>
                            {(property.property_type_name || property.property_type || 'Propiedad')}
                            {property.operation && ` en ${property.operation === 'sale' ? 'Venta' : property.operation === 'rent' ? 'Alquiler' : property.operation === 'sale_rent' || property.operation === 'sale-rent' ? 'Venta/Alquiler' : property.operation === 'vacational' ? 'Vacacional' : property.operation}`}
                        </span>
                        <span className="text-[10px] font-mono text-slate-500 dark:text-slate-400" title={property.id}>#{(property.ii_id || property.id).toString().toUpperCase()}</span>
                    </div>

                    <h3 className={clsx("text-base md:text-lg font-black truncate leading-tight mb-1", isLight ? "text-slate-900" : "text-white")}>
                        {property.title}
                    </h3>
                    <p className={clsx("text-xs flex items-center gap-1.5 truncate mb-2", isLight ? "text-slate-500" : "text-slate-400")}>
                        <MapPin size={12} className="text-indigo-400 shrink-0" />
                        {Array.from(new Set([property.sector_name, property.city_name || 'Ciudad', property.department_name || 'Guatemala'].filter(Boolean))).join(', ')}
                    </p>

                    <div className="flex items-center gap-4 mt-auto">
                        <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                            <Bed size={14} className="opacity-70" />
                            <span className="text-xs font-bold">{(property.bedrooms || 0)}{property.bedrooms_plus ? '+' : ''}</span>
                        </div>
                        {/* Land (Terreno/Finca) has no baths — varas² instead. */}
                        {isLandType(property.property_type_name) ? (
                            <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                                <LandPlot size={14} className="opacity-70" />
                                <span className="text-xs font-bold">{(property.lot_size_varas || 0)}v²</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                                <Bath size={14} className="opacity-70" />
                                <span className="text-xs font-bold">{(property.bathrooms || 0)}{property.bathrooms_plus ? '+' : ''}</span>
                            </div>
                        )}
                        <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                            <Maximize2 size={14} className="opacity-70" />
                            <span className="text-xs font-bold">{(property.built_up_area || 0)}m²</span>
                        </div>
                        {/* Assigned agent — same pill as grid mode, so list rows
                            (e.g. the Expiradas tab) show who owns the property. */}
                        {!property.sharedBy && (property.agent?.name || property.agent_name) && (
                            <div className="flex items-center gap-1.5 min-w-0 rounded-chip pr-2.5 pl-0.5 py-0.5 bg-indigo-500/10 border border-indigo-500/20">
                                {(property.agent?.avatar || property.agent_photo) ? (
                                    <img
                                        src={property.agent?.avatar || property.agent_photo}
                                        alt={property.agent?.name || property.agent_name}
                                        className="w-4 h-4 rounded-full object-cover shrink-0"
                                    />
                                ) : (
                                    <div className="w-4 h-4 rounded-full bg-indigo-500 flex justify-center items-center text-[9px] font-bold text-white shrink-0">
                                        {(property.agent?.name || property.agent_name || '?')[0]}
                                    </div>
                                )}
                                <span className="text-[10px] font-bold text-indigo-400 truncate">{property.agent?.name || property.agent_name}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Price & Actions */}
                <div className="flex flex-col items-end justify-between self-stretch py-1 pl-4 border-l border-slate-100 dark:border-white/5 shrink-0">
                    <div className="text-right">
                        <div className="flex items-center justify-end gap-2">
                            <p className={clsx("text-xl font-black tracking-tight", isLight ? "text-slate-900" : "text-white")}>
                                {formatPropertyPrice(property, displayCurrency, gtqRate)}
                            </p>
                            {property.price_label && (
                                <span className="px-1.5 py-0.5 rounded border border-amber-500/20 bg-amber-500/10 text-amber-500 text-[8px] font-black uppercase tracking-wider whitespace-nowrap">
                                    {property.price_label}
                                </span>
                            )}
                        </div>
                        {(() => {
                            const perArea = formatPricePerArea(property as any, displayCurrency, gtqRate);
                            return perArea ? (
                                <p className={clsx("text-[10px] font-semibold", isLight ? "text-slate-400" : "text-slate-500")}>
                                    {perArea}
                                </p>
                            ) : null;
                        })()}
                    </div>

                    {isArchived && (onRestore || onPermanentDelete) ? (
                        <div className="flex gap-2">
                            {onRestore && (
                                <button
                                    onClick={e => { e.stopPropagation(); onRestore(property.id, e); }}
                                    className={clsx(
                                        "h-8 px-2.5 rounded-lg border flex items-center gap-1.5 transition-colors text-[10px] font-bold uppercase",
                                        isLight
                                            ? "bg-emerald-50 border-emerald-200 text-emerald-600 hover:bg-emerald-100"
                                            : "bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"
                                    )}
                                    title="Restaurar propiedad"
                                >
                                    <RotateCcw size={12} />
                                    Restaurar
                                </button>
                            )}
                            {onPermanentDelete && (
                                <button
                                    onClick={e => { e.stopPropagation(); onPermanentDelete(property.id, e); }}
                                    className={clsx(
                                        "h-8 px-2.5 rounded-lg border flex items-center gap-1.5 transition-colors text-[10px] font-bold uppercase",
                                        isLight
                                            ? "bg-rose-50 border-rose-200 text-rose-600 hover:bg-rose-100"
                                            : "bg-rose-500/10 border-rose-500/30 text-rose-400 hover:bg-rose-500/20"
                                    )}
                                    title="Eliminar permanentemente"
                                >
                                    <Trash2 size={12} />
                                    Eliminar
                                </button>
                            )}
                        </div>
                    ) : (property.crm_status === 'draft' && onActivateDraft) ? (
                        // Draft → show the Activar shortcut INSTEAD of Publicar/Compartir.
                        // Publishing or sharing a draft without promoting crm_status leaves
                        // it stuck in the Borradores tab, which is the bug this button
                        // exists to prevent.
                        <button
                            onClick={e => { e.stopPropagation(); onActivateDraft(property.id, e); }}
                            className={clsx(
                                "h-8 px-3 rounded-lg border flex items-center gap-1.5 transition-colors text-[10px] font-bold uppercase",
                                isLight
                                    ? "bg-amber-500 border-amber-500 text-white hover:bg-amber-600"
                                    : "bg-amber-500 border-amber-500 text-white hover:bg-amber-400"
                            )}
                            title="Activar — pasará a la pestaña Activas"
                        >
                            <Power size={12} />
                            Activar
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <div className="flex flex-col items-start gap-0.5">
                                <button
                                    data-tour="share-toggle"
                                    onClick={e => { e.stopPropagation(); onShareToggle?.(property.id, e); }}
                                    className={clsx(
                                        "h-8 px-2.5 rounded-lg border flex items-center gap-1.5 transition-colors text-[10px] font-bold uppercase",
                                        property.share_status === 'shared'
                                            ? "bg-blue-500/10 text-blue-500 border-blue-500/30 hover:bg-blue-500/20"
                                            : property.share_status === 'expired'
                                                ? "bg-amber-500/10 text-amber-500 border-amber-500/30 hover:bg-amber-500/20 animate-pulse"
                                                : isLight
                                                    ? "bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100"
                                                    : "bg-white/5 border-white/10 text-slate-500 hover:bg-white/10"
                                    )}
                                    title={shouldShowRenovarShare(property) ? 'Renovar compartir' : property.share_status === 'shared' ? 'Dejar de compartir' : 'Compartir'}
                                >
                                    <Share2 size={12} />
                                    {shouldShowRenovarShare(property) ? 'Renovar' : property.share_status === 'shared' ? 'Compartida' : 'Compartir'}
                                </button>
                                {(property.share_status === 'shared' || property.share_status === 'expired') && property.share_expires_at && (() => {
                                    const exp = formatExpiry(property.share_expires_at);
                                    return <span className={clsx("text-[9px] font-medium", exp.urgent ? "text-amber-500" : isLight ? "text-slate-400" : "text-slate-500")}>{exp.text}</span>;
                                })()}
                            </div>

                            <div className="flex flex-col items-end gap-0.5">
                                <button
                                    data-tour="publish-toggle"
                                    onClick={e => { e.stopPropagation(); onPublishToggle?.(property.id, e); }}
                                    className={clsx(
                                        "h-8 px-2.5 rounded-lg border flex items-center gap-1.5 transition-colors text-[10px] font-bold uppercase",
                                        shouldShowRenovarPublish(property)
                                            ? "bg-amber-500/10 text-amber-500 border-amber-500/30 hover:bg-amber-500/20 animate-pulse"
                                            : property.publish_status === 'published'
                                                ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/20"
                                                : isLight
                                                    ? "bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100"
                                                    : "bg-white/5 border-white/10 text-slate-500 hover:bg-white/10"
                                    )}
                                    title={shouldShowRenovarPublish(property) ? 'Renovar publicación' : property.publish_status === 'published' ? 'Ocultar' : 'Publicar'}
                                >
                                    <Globe size={12} />
                                    {shouldShowRenovarPublish(property) ? 'Renovar' : property.publish_status === 'published' ? 'Publicada' : 'Publicar'}
                                </button>
                                {(property.publish_status === 'published' || property.publish_status === 'expired') && property.publish_expires_at && (() => {
                                    const exp = formatExpiry(property.publish_expires_at);
                                    return <span className={clsx("text-[9px] font-medium", exp.urgent ? "text-amber-500" : isLight ? "text-slate-400" : "text-slate-500")}>{exp.text}</span>;
                                })()}
                            </div>
                        </div>
                    )}

                    {/* Matching count badge (card mode) */}
                    {matchingCount !== undefined && property.share_status === 'shared' && (
                        <button
                            onClick={e => { e.stopPropagation(); onMatchingClick?.(e); }}
                            className={clsx(
                                "flex items-center justify-center gap-1.5 w-full py-2 rounded-xl text-[11px] font-black border transition-all mt-3",
                                isLight
                                    ? "bg-purple-50 text-purple-600 border-purple-200 hover:bg-purple-100"
                                    : "bg-purple-500/15 text-purple-400 border-purple-500/25 hover:bg-purple-500/25"
                            )}
                        >
                            <Users size={13} />
                            {matchingCount} {matchingCount === 1 ? 'Coincidencia' : 'Coincidencias'}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Intercambio (sharedBy) cards: the operation/type chip moves onto the
    // photo, and the colleague gets a contact row above Mensaje / WhatsApp.
    const isShared = !!property.sharedBy;
    const operationLabel = ['sale', 'Venta'].includes(property.operation)
        ? 'Venta'
        : ['rent', 'Alquiler'].includes(property.operation) ? 'Alquiler' : property.operation;
    const isRent = ['rent', 'Alquiler'].includes(property.operation);
    const sharedContact = isShared ? (() => {
        const agentId = (property.agent as any)?.id ?? (property as any).agent_id;
        const phoneRaw = (property.agent as any)?.phone as string | undefined;
        const hasPhone = (phoneRaw ? phoneRaw.replace(/\D/g, '') : '').length >= 8;
        const inquiry = buildIntercambioInquiry(property.title, property.ii_id);
        // WhatsApp leaves the CRM, so it carries the deep link; the in-app
        // composer doesn't — its thread header already shows the property.
        const waInquiry = buildIntercambioInquiry(property.title, property.ii_id, String(property.id));
        return {
            messageHref: agentId
                ? `/intercambio/mensajes?peer=${agentId}&property=${property.id}&draft=${encodeURIComponent(inquiry)}`
                : null,
            // WhatsApp only when the number confidently normalizes (adds the 502 for
            // bare Guatemala numbers) — a raw wa.me/<digits> is read as an intl number
            // and reports "number does not exist". Call stays permissive: see phone.ts.
            waHref: toWhatsAppNumber(phoneRaw) ? whatsAppLink(phoneRaw, waInquiry) : null,
            telHref: hasPhone && isMobileUA ? telLink(phoneRaw) : null,
        };
    })() : null;

    return (
        <div
            data-tour="property-card"
            onClick={onClick}
            className={clsx(
                "group relative rounded-3xl overflow-hidden transition-all duration-500 cursor-pointer flex flex-col h-full",
                "hover:-translate-y-2",
                isLight
                    ? "bg-white border border-slate-100 shadow-md hover:shadow-2xl hover:shadow-indigo-200/50"
                    : "bg-slate-900 border border-white/8 hover:border-indigo-500/40 hover:shadow-2xl hover:shadow-indigo-900/50",
                isSelected && (isLight ? "ring-2 ring-indigo-500 border-indigo-200 shadow-lg" : "ring-2 ring-indigo-500 border-indigo-400 shadow-xl shadow-indigo-900/50")
            )}
        >
            {/* Selection Checkbox — hidden during featured-manage mode so the
                star action takes its place (the two workflows are separate). */}
            {onSelectToggle && !featuredManageMode && (
                <button
                    onClick={onSelectToggle}
                    className={clsx(
                        "absolute top-3.5 right-3.5 z-20 p-2 rounded-xl backdrop-blur-md transition-all duration-300",
                        isSelected
                            ? "bg-indigo-600 border border-indigo-400 text-white shadow-lg"
                            : "bg-black/40 border border-white/30 text-white/50 shadow-md hover:bg-indigo-600/90 hover:border-indigo-400 hover:text-white"
                    )}
                >
                    {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                </button>
            )}

            {/* Featured-manage action — visible only in manage mode.
                - is_featured       → "Quitar Destaque" (always allowed)
                - published & slots → "Destacar" (active button)
                - published, no slots → "Destacar" disabled w/ "Sin slots" hint
                - unpublished       → locked badge "Publica para destacar" */}
            {featuredManageMode && (() => {
                const isFeatured = !!property.is_featured;
                const isPublished = property.publish_status === 'published';
                const canActivate = isPublished && featuredSlotsAvailable;
                const handle = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (!isFeatured && !canActivate) return;
                    onToggleFeatured?.(String(property.id), e);
                };
                if (!isFeatured && !isPublished) {
                    return (
                        <div className="absolute top-3.5 right-3.5 z-20 px-3 py-2 rounded-xl bg-slate-900/80 backdrop-blur-md border border-white/15 text-white/80 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 shadow-md">
                            <Lock size={12} /> Publica para destacar
                        </div>
                    );
                }
                return (
                    <div className="absolute top-3.5 right-3.5 z-20 flex items-center gap-1.5">
                        {/* Only when the agency actually holds more than one destaque
                            package — otherwise there is nothing to change it to. */}
                        {isFeatured && onSwitchFeatured && featuredCanSwitch && (
                            <button
                                onClick={e => { e.stopPropagation(); onSwitchFeatured(String(property.id), e); }}
                                disabled={featuredActioning}
                                className="px-3 py-2 rounded-xl backdrop-blur-md transition-all duration-300 text-[11px] font-black uppercase tracking-wider flex items-center gap-1.5 shadow-lg bg-amber-500/95 border border-amber-300 text-white hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed"
                                title="Cambiar el tipo de destaque (Pro / Estándar)"
                            >
                                <RefreshCw size={12} /> Cambiar
                            </button>
                        )}
                        <button
                            onClick={handle}
                            disabled={featuredActioning || (!isFeatured && !canActivate)}
                            className={clsx(
                                "px-3 py-2 rounded-xl backdrop-blur-md transition-all duration-300 text-[11px] font-black uppercase tracking-wider flex items-center gap-1.5 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed",
                                isFeatured
                                    ? "bg-rose-500/95 border border-rose-300 text-white hover:bg-rose-500"
                                    : canActivate
                                        ? "bg-amber-500/95 border border-amber-300 text-white hover:bg-amber-400"
                                        : "bg-slate-900/70 border border-white/20 text-white/60"
                            )}
                            title={isFeatured ? 'Quitar destaque' : canActivate ? 'Destacar propiedad' : 'No hay slots disponibles'}
                        >
                            {featuredActioning ? (
                                <Loader2 size={12} className="animate-spin" />
                            ) : isFeatured ? (
                                <><Star size={12} fill="currentColor" /> Quitar</>
                            ) : (
                                <><Star size={12} fill={canActivate ? 'currentColor' : 'none'} /> Destacar</>
                            )}
                        </button>
                    </div>
                );
            })()}

            {/* ────────────────────────────────────────
                1. HERO IMAGE  (clean — no text)
            ──────────────────────────────────────── */}
            <div className="relative aspect-[4/3] sm:aspect-[16/10] overflow-hidden shrink-0">
                {coverSrc ? (
                    <img
                        src={coverSrc}
                        alt={property.title}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                    />
                ) : (
                    <div className={clsx("w-full h-full flex items-center justify-center", isLight ? "bg-slate-100 text-slate-300" : "bg-slate-800 text-slate-600")}>
                        <MapPin size={48} />
                    </div>
                )}

                {/* Subtle top vignette for depth only */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/10 pointer-events-none" />

                {/* Top badges: always on the left now since checkbox is on the right */}
                <div className="absolute top-3.5 left-3.5 flex items-center gap-2">
                    {isArchived && (
                        <span className="px-2.5 py-1 rounded-chip bg-slate-900/85 backdrop-blur-md border border-white/30 text-[10px] font-black uppercase tracking-widest text-white shadow-lg">
                            {archivedLabel}
                        </span>
                    )}
                    {property.is_featured && (
                        <span className="px-2.5 py-1 rounded-chip bg-amber-500/90 backdrop-blur-md border border-amber-400/50 text-[10px] font-bold uppercase tracking-widest text-white flex items-center gap-1 shadow-lg">
                            <Sparkles size={9} /> Destacada
                        </span>
                    )}
                </div>

                {isArchived && (
                    <div className="absolute inset-0 bg-slate-900/30 pointer-events-none" />
                )}

                {/* Intercambio: operation · type chip sits on the photo instead
                    of taking its own row in the body. */}
                {isShared && (
                    <span className={clsx(
                        "absolute bottom-3 left-3.5 px-2.5 py-1 rounded-chip bg-white/95 backdrop-blur-md text-[10px] font-bold uppercase tracking-wider shadow-md",
                        operationLabel === 'Venta' ? "text-indigo-700" : "text-teal-700"
                    )}>
                        {operationLabel} · {(property.property_type_name || property.property_type || 'Propiedad')}
                    </span>
                )}
            </div>

            {/* ────────────────────────────────────────
                2. COLORED INFO BAR  (ID · beds · baths · m²)
            ──────────────────────────────────────── */}
            <div className="relative z-10">
                {/* The bar itself */}
                <div className={clsx(
                    "flex items-center h-11 divide-x divide-white/20",
                    statusMeta.bar
                )}>
                    {/* Status Picker (hidden on Intercambio — not your property to change) + ID */}
                    <div className="flex items-center gap-2 pl-2 pr-3 shrink-0 h-full" onClick={e => e.stopPropagation()}>
                        {!property.sharedBy && (
                            <StatusPicker
                                propertyId={property.id}
                                currentStatus={(property.status || 'Disponible')}
                                onStatusChange={onStatusChange}
                            />
                        )}
                        <div className="flex items-center gap-1 opacity-80" title={property.ii_id ? `${property.ii_id} · ${property.id}` : property.id}>
                            <Hash size={11} className="text-white/70" />
                            <span className="text-[11px] font-black tracking-wider font-mono text-white/90">
                                {property.ii_id
                                    ? property.ii_id.toUpperCase()
                                    : (property.id.toUpperCase().length > 8 ? `${property.id.toUpperCase().substring(0, 8)}...` : property.id.toUpperCase())}
                            </span>
                        </div>
                    </div>

                    <div className="flex-1 min-w-0" />

                    {/* Specs */}
                    <div className="flex items-stretch h-full divide-x divide-white/10">
                        <div className="flex items-center gap-1.5 px-3 text-white/90">
                            <Bed size={13} className="opacity-80" />
                            <span className="text-xs font-bold">{(property.bedrooms || 0)}{property.bedrooms_plus ? '+' : ''}</span>
                            <span className="text-[10px] text-white/60 hidden sm:inline">hab</span>
                        </div>
                        {isLandType(property.property_type_name) ? (
                            <div className="flex items-center gap-1.5 px-3 text-white/90">
                                <LandPlot size={13} className="opacity-80" />
                                <span className="text-xs font-bold">{(property.lot_size_varas || 0)}</span>
                                <span className="text-[10px] text-white/60 hidden sm:inline">v²</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1.5 px-3 text-white/90">
                                <Bath size={13} className="opacity-80" />
                                <span className="text-xs font-bold">{(property.bathrooms || 0)}{property.bathrooms_plus ? '+' : ''}</span>
                                <span className="text-[10px] text-white/60 hidden sm:inline">baños</span>
                            </div>
                        )}
                        <div className="flex items-center gap-1.5 px-3 text-white/90">
                            <Maximize2 size={13} className="opacity-80" />
                            <span className="text-xs font-bold">{(property.built_up_area || 0)}</span>
                            <span className="text-[10px] text-white/60 hidden sm:inline">m²</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ────────────────────────────────────────
                3. CARD BODY  (title · operation · type · location)
            ──────────────────────────────────────── */}
            <div className={clsx("px-5 pt-4 pb-1 grow flex flex-col", isLight ? "bg-white" : "bg-slate-900")}>

                {/* Operation + Type row (on the photo for Intercambio cards) */}
                {!isShared && (
                <div className="flex items-center gap-2 mb-2.5 flex-wrap">
                    {/* Operation pill: Venta or Alquiler */}
                    <span className={clsx(
                        "px-2.5 py-0.5 rounded-chip text-[10px] font-bold uppercase tracking-wider border",
                        ['sale', 'Venta'].includes(property.operation)
                            ? isLight
                                ? "bg-indigo-50 text-indigo-600 border-indigo-200"
                                : "bg-indigo-500/15 text-indigo-400 border-indigo-500/25"
                            : isLight
                                ? "bg-teal-50 text-teal-600 border-teal-200"
                                : "bg-teal-500/15 text-teal-400 border-teal-500/25"
                    )}>
                        {property.operation === 'sale' ? 'Venta' : property.operation === 'rent' ? 'Alquiler' : property.operation}
                    </span>

                    <span className={clsx("w-px h-3.5 shrink-0", isLight ? "bg-slate-200" : "bg-white/10")} />

                    {/* Type chip */}
                    <span className={clsx(
                        "px-2.5 py-0.5 rounded-chip text-[10px] font-bold uppercase tracking-wider",
                        type.bg, type.text
                    )}>
                        {(property.property_type_name || property.property_type || 'Propiedad')}
                    </span>
                </div>
                )}

                {/* Title — two lines on Intercambio cards, where the title
                    carries details the colleague can't otherwise see. */}
                <h3
                    className={clsx(
                        "text-base font-black leading-snug tracking-tight mb-1",
                        isShared ? "line-clamp-2" : "truncate",
                        isLight ? "text-slate-900" : "text-white"
                    )}
                    title={isShared ? property.title : undefined}
                >
                    {property.title}
                </h3>

                {/* Location */}
                <p className={clsx(
                    "text-xs flex items-center gap-1 truncate mb-3",
                    isLight ? "text-slate-400" : "text-slate-500"
                )}>
                    <MapPin size={11} className="text-indigo-400 shrink-0" />
                    {Array.from(new Set([property.sector_name, property.city_name || 'Ciudad', property.department_name || 'Guatemala'].filter(Boolean))).join(', ')}
                </p>

                {/* Price block — directly below location */}
                <div className="mb-3">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <p className={clsx(
                            "text-2xl font-black tracking-tight leading-none",
                            isLight ? "text-slate-900" : "text-white"
                        )}>
                            {formatPropertyPrice(property, displayCurrency, gtqRate)}
                        </p>
                        {/* Intercambio: "/mes" and price per area share the price line */}
                        {isShared && (() => {
                            const perArea = formatPricePerArea(property as any, displayCurrency, gtqRate);
                            const suffix = [isRent ? '/mes' : null, perArea?.replace(' / ', '/')].filter(Boolean).join(' · ');
                            return suffix ? (
                                <span className={clsx("text-xs font-semibold", isLight ? "text-slate-400" : "text-slate-500")}>
                                    {suffix}
                                </span>
                            ) : null;
                        })()}
                        {property.price_label && (
                            <span className="px-1.5 py-0.5 rounded-chip bg-amber-500/10 text-amber-500 text-[8px] font-black uppercase tracking-wider border border-amber-500/20">
                                {property.price_label}
                            </span>
                        )}
                    </div>
                    {!isShared && (() => {
                        const perArea = formatPricePerArea(property as any, displayCurrency, gtqRate);
                        return perArea ? (
                            <p className={clsx("text-[10px] font-semibold", isLight ? "text-slate-400" : "text-slate-500")}>
                                {perArea}
                            </p>
                        ) : null;
                    })()}
                </div>

                {/* Assigned agent — pill matching the sharedBy style. CRMContext
                    populates property.agent ({ name, avatar, ... }) from
                    profiles!properties_agent_id_fkey. Without this, properties
                    on V2 lost the visible agent badge that legacy showed.
                    Hidden on intercambio cards: the sharedBy pill below already
                    surfaces the same person as "De [name] · Hace X". */}
                {!property.sharedBy && (property.agent?.name || property.agent_name) && (
                    <div className="flex items-center gap-2 mb-3 overflow-hidden w-fit rounded-chip pr-3 pl-1 py-1 bg-indigo-500/10 border border-indigo-500/20 mt-auto">
                        {(property.agent?.avatar || property.agent_photo) ? (
                            <img
                                src={property.agent?.avatar || property.agent_photo}
                                alt={property.agent?.name || property.agent_name}
                                className="w-5 h-5 rounded-full object-cover shrink-0"
                            />
                        ) : (
                            <div className="w-5 h-5 rounded-full bg-indigo-500 flex justify-center items-center text-[10px] font-bold text-white shrink-0">
                                {(property.agent?.name || property.agent_name || '?')[0]}
                            </div>
                        )}
                        <span className="text-[10px] font-bold text-indigo-400 truncate">{property.agent?.name || property.agent_name}</span>
                    </div>
                )}

                {/* Intercambio colleague — who shared it, from which agency, when.
                    Llamar (mobile UA only) rides along as an icon button. */}
                {property.sharedBy && (() => {
                    const sb = property.sharedBy;
                    const when = (property.shared_at || property.created_at) ? formatRelativeEs(property.shared_at || property.created_at) : null;
                    const subline = [shouldShowAgencyName(sb.name, sb.agencyName) ? sb.agencyName : null, when].filter(Boolean).join(' · ');
                    return (
                        <div className={clsx(
                            "flex items-center gap-3 mt-auto mb-3 pt-3 border-t",
                            isLight ? "border-slate-100" : "border-white/5"
                        )}>
                            <ColleagueAvatar
                                name={sb.name}
                                avatar={sb.avatar}
                                agencyLogo={sb.agencyLogo}
                                agencyLogoBg={sb.agencyLogoBg}
                                agencyName={sb.agencyName}
                                isLight={isLight}
                            />
                            <div className="min-w-0 flex-1">
                                <p className={clsx("text-sm font-bold truncate", isLight ? "text-slate-900" : "text-white")}>{sb.name}</p>
                                {subline && (
                                    <p className={clsx("text-xs truncate", isLight ? "text-slate-500" : "text-slate-400")}>{subline}</p>
                                )}
                            </div>
                            {sharedContact?.telHref && (
                                <a
                                    href={sharedContact.telHref}
                                    onClick={e => e.stopPropagation()}
                                    title="Llamar al colega"
                                    aria-label="Llamar al colega"
                                    className={clsx(
                                        "shrink-0 w-9 h-9 flex items-center justify-center rounded-xl border transition-all",
                                        isLight
                                            ? "border-slate-200 text-slate-600 hover:bg-slate-50"
                                            : "border-white/10 text-slate-300 hover:bg-white/5"
                                    )}
                                >
                                    <Phone size={15} />
                                </a>
                            )}
                        </div>
                    );
                })()}
            </div>

            {/* ────────────────────────────────────────
                4. FOOTER  (price · status pills · CTA)
            ──────────────────────────────────────── */}
            <div className={clsx(
                "px-5 pb-5 shrink-0",
                isLight ? "bg-white" : "bg-slate-900"
            )}>
                {/* Divider — hidden on intercambio cards (other agencies' shared properties) */}
                {!property.sharedBy && (
                    <div className={clsx("h-px mb-3", isLight ? "bg-slate-100" : "bg-white/5")} />
                )}

                {/* Intercambio contact actions — replaces the owner-side
                    publish/share pills (which are hidden for sharedBy cards).
                    Mensaje opens the in-app conversation with the property
                    title + ii_id pre-filled in the composer. WhatsApp (the
                    primary action) opens wa.me with the same text. Llamar
                    lives in the colleague row above. */}
                {sharedContact && (sharedContact.messageHref || sharedContact.waHref) && (
                    <div className="flex flex-wrap items-center gap-2 mb-1" onClick={e => e.stopPropagation()}>
                        {sharedContact.messageHref && (
                            <button
                                onClick={() => navigate(sharedContact.messageHref!)}
                                title="Escribirle al colega"
                                className={clsx(
                                    "flex-1 min-w-[88px] flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold transition-all border",
                                    isLight
                                        ? "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                                        : "bg-transparent border-white/15 text-slate-200 hover:bg-white/5"
                                )}
                            >
                                <MessageSquare size={14} />
                                Mensaje
                            </button>
                        )}
                        {sharedContact.waHref && (
                            <a
                                href={sharedContact.waHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Enviar WhatsApp al colega"
                                className="flex-1 min-w-[88px] flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold transition-all border bg-green-600 border-green-600 text-white hover:bg-green-700 hover:border-green-700"
                            >
                                <MessageCircle size={14} />
                                WhatsApp
                            </a>
                        )}
                    </div>
                )}

                {/* Archived: quick-action pills (Restaurar / Eliminar) replace publish/share since those don't apply to soft-deleted rows */}
                {!property.sharedBy && isArchived && (onRestore || onPermanentDelete) && (
                <div className="flex flex-wrap items-center gap-1.5 mb-3 justify-end">
                    {onRestore && (
                        <button
                            onClick={e => { e.stopPropagation(); onRestore(property.id, e); }}
                            className={clsx(
                                "flex items-center gap-1 px-2.5 py-1 rounded-chip text-[10px] font-bold border hover:opacity-80 transition-opacity",
                                isLight ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-emerald-500/10 text-emerald-400 border-emerald-500/25"
                            )}
                        >
                            <RotateCcw size={9} />
                            Restaurar
                        </button>
                    )}
                    {onPermanentDelete && (
                        <button
                            onClick={e => { e.stopPropagation(); onPermanentDelete(property.id, e); }}
                            className={clsx(
                                "flex items-center gap-1 px-2.5 py-1 rounded-chip text-[10px] font-bold border hover:opacity-80 transition-opacity",
                                isLight ? "bg-rose-50 text-rose-600 border-rose-200" : "bg-rose-500/10 text-rose-400 border-rose-500/25"
                            )}
                        >
                            <Trash2 size={9} />
                            Eliminar
                        </button>
                    )}
                </div>
                )}

                {/* Publish/Share pills — hidden on intercambio cards (owner-side actions don't apply) and on archived cards (replaced by Restaurar/Eliminar above) */}
                {!property.sharedBy && !isArchived && property.crm_status === 'draft' && onActivateDraft && (
                <div className="flex flex-wrap items-center gap-1.5 mb-3 justify-end">
                    <button
                        onClick={e => { e.stopPropagation(); onActivateDraft(property.id, e); }}
                        className={clsx(
                            "flex items-center gap-1 px-2.5 py-1 rounded-chip text-[10px] font-bold border hover:opacity-80 transition-opacity",
                            isLight
                                ? "bg-amber-500 text-white border-amber-500"
                                : "bg-amber-500 text-white border-amber-500"
                        )}
                        title="Activar — pasará a la pestaña Activas"
                    >
                        <Power size={9} />
                        Activar
                    </button>
                </div>
                )}
                {!property.sharedBy && !isArchived && property.crm_status !== 'draft' && (
                <div className="flex flex-wrap items-center gap-1.5 mb-3">
                        <button
                            onClick={e => { e.stopPropagation(); onPublishToggle?.(property.id, e); }}
                            className={clsx(
                                "flex items-center gap-1 px-2.5 py-1 rounded-chip text-[10px] font-bold border hover:opacity-80 transition-opacity",
                                shouldShowRenovarPublish(property)
                                    ? "bg-amber-500/10 text-amber-400 border-amber-500/25 animate-pulse"
                                    : property.publish_status === 'published'
                                        ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/25"
                                        : isLight ? "bg-slate-100 text-slate-400 border-slate-200" : "bg-white/5 text-slate-500 border-white/10"
                            )}
                        >
                            <Globe size={9} />
                            {shouldShowRenovarPublish(property)
                                ? property.publish_expires_at
                                    ? `Renovar · ${formatExpiry(property.publish_expires_at).text}`
                                    : 'Renovar'
                                : property.publish_status === 'published'
                                    ? property.publish_expires_at
                                        ? `Publicada — ${formatExpiry(property.publish_expires_at).text}`
                                        : 'Publicada'
                                    : 'No publicada'}
                        </button>
                        <button
                            onClick={e => { e.stopPropagation(); onShareToggle?.(property.id, e); }}
                            className={clsx(
                                "flex items-center gap-1 px-2.5 py-1 rounded-chip text-[10px] font-bold border hover:opacity-80 transition-opacity",
                                // Colour follows the LABEL, not a second
                                // threshold: a chip that reads "Renovar" while
                                // painted passive-blue is the confusion this
                                // change exists to remove.
                                property.share_status === 'expired'
                                    ? "bg-amber-500/10 text-amber-400 border-amber-500/25 animate-pulse"
                                    : shouldShowRenovarShare(property)
                                        ? "bg-amber-500/10 text-amber-400 border-amber-500/25"
                                        : property.share_status === 'shared'
                                            ? "bg-blue-500/10 text-blue-400 border-blue-500/25"
                                            : isLight ? "bg-slate-100 text-slate-400 border-slate-200" : "bg-white/5 text-slate-500 border-white/10"
                            )}
                        >
                            <Share2 size={9} />
                            {shouldShowRenovarShare(property)
                                ? property.share_expires_at
                                    ? `Renovar · ${formatExpiry(property.share_expires_at).text}`
                                    : 'Renovar'
                                : property.share_status === 'shared'
                                    ? property.share_expires_at
                                        ? `Compartida — ${formatExpiry(property.share_expires_at).text}`
                                        : 'Compartida'
                                    : 'No compartida'}
                        </button>
                        {property.share_status === 'shared' && matchingCount !== undefined && (
                            <button
                                onClick={e => { e.stopPropagation(); onMatchingClick?.(e); }}
                                className={clsx(
                                    "flex items-center gap-1.5 px-2.5 py-1 rounded-chip text-[10px] font-black border transition-all hover:opacity-80 cursor-pointer",
                                    isLight ? "bg-purple-50 text-purple-600 border-purple-200 shadow-sm" : "bg-purple-500/20 text-purple-400 border-purple-500/30 shadow-lg shadow-purple-500/10"
                                )}>
                                <Users size={10} />
                                <span>{matchingCount} {matchingCount === 1 ? 'Coincidencia' : 'Coincidencias'}</span>
                            </button>
                        )}
                        {property.created_at && (
                            <span className={clsx(
                                "ml-auto text-[10px] font-medium whitespace-nowrap",
                                isLight ? "text-slate-400" : "text-slate-500"
                            )}>
                                Creado: {formatRelativeEs(property.created_at)}
                            </span>
                        )}
                </div>
                )}

                {/* Expiry dates */}
                {(property.publishExpiryDate || property.shareExpiryDate) && (
                    <div className={clsx(
                        "flex items-center gap-4 px-3 py-2 rounded-xl mb-4 text-[10px] font-medium",
                        isLight ? "bg-slate-50 text-slate-400" : "bg-white/4 text-slate-500"
                    )}>
                        {property.publishExpiryDate && (
                            <span className="flex items-center gap-1">
                                <Globe size={9} className="text-emerald-400" />
                                Exp: {property.publishExpiryDate}
                            </span>
                        )}
                        {property.shareExpiryDate && (
                            <span className="flex items-center gap-1">
                                <Share2 size={9} className="text-blue-400" />
                                Exp: {property.shareExpiryDate}
                            </span>
                        )}
                    </div>
                )}

                {showVerificar && (
                    <button
                        onClick={async e => {
                            e.stopPropagation();
                            if (!supabase || creatingRenewal) return;
                            setCreatingRenewal(true);
                            try {
                                const { data, error } = await supabase.rpc('create_renewal_token', { p_property_id: property.id });
                                if (error) throw error;
                                const renewUrl = `https://intercambioinmobiliario.com/renovar/${data.token_id}`;
                                const message = `Estimado(a) ${data.owner_name}, de parte de ${data.agent_name}.\n\nLa promoción de tu propiedad "${data.property_title || ''}" está por vencer. Puedes renovarla o retirarla del mercado en el siguiente enlace:\n${renewUrl}`;
                                window.open(whatsAppLink(property.owner?.phone, message), '_blank');
                            } catch (err: any) {
                                alert(`Error: ${err?.message || 'No se pudo crear el enlace'}`);
                            } finally {
                                setCreatingRenewal(false);
                            }
                        }}
                        disabled={creatingRenewal}
                        className={clsx(
                            "w-full py-2.5 rounded-xl flex items-center justify-center gap-1.5 text-[10px] font-bold transition-all border",
                            isLight
                                ? "bg-emerald-50 border-emerald-200 text-emerald-600 hover:bg-emerald-100 hover:shadow-sm"
                                : "bg-emerald-500/10 border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20",
                            creatingRenewal && "opacity-50"
                        )}
                        title="Verificar Disponibilidad con Propietario"
                    >
                        {creatingRenewal ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />}
                        <span>Verificar</span>
                    </button>
                )}
            </div>
        </div>
    );
};

// Memoize so the grid + kanban don't re-render every card when an unrelated
// piece of CRMContext state updates. CRMContext rebuilds the `property` object
// on every fetch (new reference), so a shallow compare wouldn't help — this
// custom comparator checks only the fields the card actually renders.
export const PropertyCard = React.memo(PropertyCardImpl, (prev, next) => {
    if (prev.theme !== next.theme) return false;
    if (prev.isSelected !== next.isSelected) return false;
    if (prev.isListMode !== next.isListMode) return false;
    if (prev.matchingCount !== next.matchingCount) return false;
    if (prev.featuredManageMode !== next.featuredManageMode) return false;
    if (prev.featuredSlotsAvailable !== next.featuredSlotsAvailable) return false;
    if (prev.featuredActioning !== next.featuredActioning) return false;

    const a = prev.property;
    const b = next.property;
    if (a === b) return true;
    if (a.id !== b.id) return false;
    if (a.title !== b.title) return false;
    if (a.price !== b.price) return false;
    if (a.status !== b.status) return false;
    if (a.property_status !== b.property_status) return false;
    if (a.publish_status !== b.publish_status) return false;
    if (a.share_status !== b.share_status) return false;
    if (a.is_deleted !== b.is_deleted) return false;
    if (a.is_featured !== b.is_featured) return false;
    if (a.cover_photo_url !== b.cover_photo_url) return false;
    if (a.publish_expires_at !== b.publish_expires_at) return false;
    if (a.matchesCount !== b.matchesCount) return false;
    if (a.bedrooms !== b.bedrooms) return false;
    if (a.bathrooms !== b.bathrooms) return false;
    if (a.built_up_area !== b.built_up_area) return false;
    if (a.lot_size_varas !== b.lot_size_varas) return false;
    if (a.city_name !== b.city_name) return false;
    if (a.sector_name !== b.sector_name) return false;
    if (a.property_type_name !== b.property_type_name) return false;
    if (a.operation !== b.operation) return false;
    if (a.ii_id !== b.ii_id) return false;
    if (a.agent_id !== b.agent_id) return false;
    if (a.agent_name !== b.agent_name) return false;
    if ((a as any).sharedBy?.name !== (b as any).sharedBy?.name) return false;
    return true;
});
